import { useCallback, useMemo, useState } from "react";
import { Col, Row } from '../common/components/Grid';
import useClientConfig from "../common/hooks/clientConfigHook";
import { JobAssistance } from "./assistance/JobAssistance";
import JobDecisions from "./JobDecisions";
import JobDetails from "./JobDetails";
import { JobForms } from "./JobForms";
import JobImages from "./JobImages";
import JobNote from "./JobNote";
import JobRelations from "./JobRelations";
import JobRelatedDocs from "./JobRelatedDoc";
import PropTypes from 'prop-types';
import { useJobAssistance } from "../reducers/jobAssistanceSlice";
import useCredentials from "../common/hooks/credentialsHook";
import { JobEpcrs } from "./JobEpcrs";
import OfflineMask from "../common/components/OfflineMask";
import { useSpecializedTranslation } from "../hooks/specializedTranslationHook";
import { checkEnabled } from "../utils/enableUtilities";
import { canLogJob, canUpdateJob, updateJob } from "../reducers/jobSlice";
import useNetwork from "../common/hooks/networkHook";
import { AdminJobRequestsCard, JobRequestProvider } from "./JobRequestsPanel";
import TabBar from "../common/components/TabBar";
import { useTypes } from "../reducers/typeSlice";
import { getDisplayTypes } from "./TypeDropdown";

/**
 * Base panel for showing all the details of a job 
 * @component
 */
const Job = ({job}) => {
    const assistance = useJobAssistance(job.jobid);
    const { t } = useSpecializedTranslation(job);
    const clientConfig = useClientConfig();
    const credentials = useCredentials();
    const network = useNetwork();
    const types = useTypes();

    const anyCreate = useMemo(() => {
        return getDisplayTypes(types, credentials.access, null, null, null, 'write', 'create')?.length > 0
    }, [types, credentials]);

    const hasFullAccess = useMemo(() => {
        return canUpdateJob(credentials, clientConfig, job, assistance);
    }, [credentials, clientConfig, job, assistance]);
    const hasLogAccess = useMemo(() => {
        return canLogJob(credentials, clientConfig, job, assistance);
    }, [credentials, clientConfig, job, assistance]);

    const runUpdate = useCallback(delta => {
        updateJob(network, {...delta, jobid: job.jobid })
    }, [network, job.jobid]);

    const showConv = useMemo(() => job.channel === 'SMS' || job.channel === 'Email' || job.channel === 'Voice', [job?.channel]);

    const [customSections, setCustomSections] = useState({});

    const registerSection = useCallback((section) => {
        setCustomSections(sections => ({ ...sections, [section.id]: section }))
        return () => setCustomSections(sections => {
            const newSections = { ...sections };
            delete newSections[section.id];
            return newSections;
        })
    }, []);

    const allSections = useMemo(() => {
        const hasJob = !!job?.jobid;

        const baseSections = [
            {
                id: 'conv',
                label: 'conversation',
                icon: { variant: 'regular', kind: 'comment' },
                enabled: () => hasJob && showConv,
                render: () => (
                    <JobNote jobid={job.jobid} convMode disabled={!hasLogAccess && !hasFullAccess} />
                ),
                sequence: 100,
            },
            {
                id: 'notes',
                label: 'notes',
                icon: { variant: 'solid', kind: 'clipboard-list' },
                enabled: () => hasJob,
                render: () => (
                    <JobNote jobid={job.jobid} disabled={!hasLogAccess && !hasFullAccess} />
                ),
                sequence: 200,
            },
            {
                id: 'assistance',
                label: 'jobAssistance',
                icon: { variant: 'solid', kind: 'handshake-simple' },
                enabled: () =>
                    checkEnabled(clientConfig, clientConfig.enableAssistance, job) &&
                    (hasJob || checkEnabled(clientConfig, clientConfig.assistanceFirstClass, job)),
                render: () => (
                    <div style={{ maxHeight: 'max(300px, 50vh)', overflowY: 'auto' }}>
                        <JobAssistance job={job} hideLabel={true} disabled={!hasFullAccess} />
                    </div>
                ),
                sequence: 300,
            },
            {
                id: 'access',
                label: 'submittedJobAccessRequests',
                icon: { variant: 'solid', kind: 'door-open' },
                enabled: () => hasJob && checkEnabled(clientConfig, clientConfig.requestableJobs.enable, job),
                render: () => (
                    <>
                        <OfflineMask supportsOffline={false} />
                        <JobRequestProvider job={job}>
                            <AdminJobRequestsCard
                                jobid={job.jobid}
                                assistance={assistance}
                                canUpdate={hasFullAccess}
                            />
                        </JobRequestProvider>
                    </>
                ),
                sequence: 400,
            },
            {
                id: 'relations',
                label: 'jobRelations',
                icon: { variant: 'solid', kind: 'link' },
                enabled: () => hasJob && checkEnabled(clientConfig, clientConfig.jobLinking, job),
                render: () => <JobRelations jobid={job.jobid} disabled={!hasFullAccess} />,
                sequence: 500,
            },
            {
                id: 'pcr',
                label: 'epcr_one',
                icon: { variant: 'solid', kind: 'heart-pulse' },
                enabled: () => hasJob && !!clientConfig.enablePCR,
                render: () => (
                    <JobEpcrs
                        jobid={job.jobid}
                        disabled={!hasFullAccess}
                        style={{ maxHeight: 'max(300px, 50vh)', overflowY: 'auto' }}
                    />
                ),
                sequence: 600,
            },
            {
                id: 'forms',
                label: 'jobForms',
                icon: { variant: 'solid', kind: 'file-contract' },
                enabled: () => hasJob && checkEnabled(clientConfig, clientConfig.enableJobForms, job),
                render: () => (
                    <JobForms jobid={job.jobid} disabled={!hasFullAccess} style={{ maxHeight: 'max(300px, 50vh)' }} />
                ),
                sequence: 700,
            },
            {
                id: 'images',
                label: 'jobImages',
                icon: { variant: 'regular', kind: 'image' },
                enabled: () => hasJob && checkEnabled(clientConfig, clientConfig.enableJobPhotos, job),
                render: () => <JobImages jobid={job.jobid} disabled={!hasFullAccess} />,
                sequence: 800,
            },
            {
                id: 'attachment',
                label: 'jobRelatedContent',
                icon: { variant: 'solid', kind: 'paperclip' },
                enabled: () => hasJob, // you always add it when jobid exists
                render: () => (
                    <>
                        <OfflineMask supportsOffline={false} />
                        <JobRelatedDocs
                            jobid={job.jobid}
                            disabled={!hasFullAccess}
                            style={{ maxHeight: 'max(300px, 50vh)' }}
                        />
                    </>
                ),
                sequence: 900,
            },
            {
                id: 'decisions',
                label: 'jobDecisions',
                icon: { variant: 'solid', kind: 'scale-balanced' },
                enabled: () => hasJob && checkEnabled(clientConfig, clientConfig.decision?.enable, job),
                render: () => <JobDecisions jobid={job.jobid} style={{ maxHeight: 'max(300px, 50vh)' }} />,
                sequence: 1000,
            },
        ];

        return baseSections.concat(Object.values(customSections));
    }, [ job, showConv, clientConfig, hasLogAccess, hasFullAccess, assistance, customSections ]);

    const sections = useMemo(() => {
        const sections = allSections.filter(s => s.enabled());
        sections.sort((a, b) => a.sequence - b.sequence);
        return sections;
    }, [allSections]);

    if (!job?.jobid && !anyCreate) {
        return (<div className="w-100">
            <Row>
                <Col>
                {t('selectJob')}
                </Col>
            </Row>
        </div>)
    }

    return (<div className="w-100">
        <Row>
            <Col>
                <JobDetails job={job ?? {}} disabled={!hasFullAccess} onChange={runUpdate} registerSection={registerSection}></JobDetails>
            </Col>
        </Row>
        { sections?.length > 0 && <Row className="my-3">
            <Col>
            <TabBar
                id="jobTabs"
                buttons={sections}
                showLabels
                enableCollapse
                t={t}
            >
            </TabBar>
            </Col>
        </Row> }
    </div>);
}

Job.propTypes = {
    /**
     * The job the panel should display.
     */
    job: PropTypes.object.isRequired,
}

export default Job